import { SxProps, Theme } from '@mui/material';
import { EIconButtons } from './EasTableRow/EasTableValue/constants';

export type TEasTableProps<T extends ItemWithId> = {
	tableStructure: TTableStructure<keyof T, T>[];
	itemsIsLoading: boolean;
	items: T[];
	handleAction: THandleAction;
	handleLink?: (id: number) => void;
	skeletonRows?: any[];
	highlightedRow?: number | null;
	disabledRowFn?: (item: T) => boolean | undefined;
};

export interface ItemWithId {
	id: number;
}

export enum ETableStructureType {
	EM = 'em',
	EMAIL = 'email',
	ORDER_ID = 'orderId',
	INVOICE_NUMBER = 'invoiceNumber',
	IOSS_NUMBER = 'iossNumber',
	STRING = 'string',
	TEXT = 'text',
	COPYABLE_TEXT = 'copyableText',
	MULTILINE_TEXT = 'multilineText',
	COMMENTS = 'comments',
	LINK = 'link',
	NUMBER = 'number',
	ARRAY_OF_OBJECTS = 'arrayOfObjects',
	DATE = 'date',
	DATE_TIME = 'dateTime',
	DATE_PERIOD = 'datePeriod',
	DATE_RANGE = 'dateRange',
	DATE_MONTH = 'dateMonth',
	USER = 'user',
	USERS = 'users',
	STATUS = 'status',
	STATUS_OBJECT = 'statusObject',
	STATUS_NO_BG = 'statusNoBg',
	STATUS_ARRAY = 'statusArray',
	AMOUNT = 'amount',
	CURRENCY = 'currency',
	COUNTRY = 'country',
	COUNTRY_EU = 'countryEU',
	COUNTRY_CODE = 'countryCode',
	COUNTRY_CODE_EU = 'countryCodeEU',
	COUNTRY_CODE_ARRAY = 'countryCodeArray',
	COUNTRY_NUMBER = 'countryNumber',
	COUNTRY_NAME = 'countryName',
	BOOLEAN = 'boolean',
	BUTTON = 'button',
	BUTTONS = 'buttons',
	ICON = 'icon',
	ICON_ARRAY = 'iconArray',
	ARRAY_ID = 'arrayId',
	CHECKBOX = 'checkbox',
	STATUS_ARRAY_VERTICAL = 'statusArrayVertical',
}

export enum ECountryType {
	ALL = 'All',
	EU = 'EU',
	NON_EU = 'NON-EU',
}
export enum ETableStructureAlign {
	CENTER = 'center',
	LEFT = 'left',
	RIGHT = 'right',
}

export enum ETableStructureVerticalAlign {
	TOP = 'top',
	MIDDLE = 'middle',
	BASELINE = 'baseline',
}

export enum ESortDirection {
	ASC = 'asc',
	DESC = 'desc',
}

export type TEmValue = {
	id: number;
	name: string;
};

export type TTableStructureFilter = [string | null | number, string][];
export type TTableStructureColors = [string | null | number, string][];

export type TTableStructureButton = {
	name: string;
	label?: string;
	icon?: EIconButtons;
	color?: string;
};

export type TStatusObject = {
	id: number;
	statusName: string;
	statusCode?: string;
};

export type TTableStructure<TSelector, TItem = any> = {
	name: string;
	selector: TSelector | 'actions';
	value?: (item: TItem) => string | TEmValue | string[] | boolean;
	show: boolean;
	showInFoolData?: boolean;
	searchable?: boolean;
	sortable?: boolean;
	filterable?: boolean;
	separateFilter?: boolean;
	filter?: TTableStructureFilter;
	defaultFilterValue?: string;
	/**
	 * Value a `separateFilter` column is filtered by while the url carries no `f-<selector>` param.
	 * Clearing the filter leaves an empty param behind, which keeps the default from coming back.
	 */
	defaultSeparateFilterValue?: string;
	/** Picks the value of a `separateFilter` column from `filter` by typing instead of by menu. */
	separateFilterAutocomplete?: boolean;
	colors?: TTableStructureColors;
	colSpan?: number;
	sxTableCell?: SxProps<Theme>;
	type: ETableStructureType;
	align: ETableStructureAlign;
	verticalAlign?: ETableStructureVerticalAlign;
	noName?: boolean;
	buttons?: TTableStructureButton[];
	buttonFilter?: (item: TItem) => TTableStructureButton[];
	icons?: {
		value: boolean | string | null;
		icon: EIconButtons;
		color?: string;
	}[];
	currency?: (item: TItem) => string;
	key?: boolean;
	editable?: boolean;
	disabledRowFn?: (item: TItem) => boolean | undefined;
	enumName?: string;
	tooltip?: (item: TItem) => string;
};

export type THandleAction = (id: number, typeOfDialog: string) => void;
